/**
 * useAuth — central hook for reading current session.
 *
 * Calls /api/me on mount, caches the result in context. Child components
 * read { admin, tenant, loading } off useAuth() without each one making
 * their own /api/me request.
 *
 * Phase 1 scope: just the hydration + refresh. No login/logout methods
 * on the context — those are one-off page actions (LoginPage, logout
 * button) that don't need to be shared primitives. They just hit the API
 * directly and call refresh() from the hook to re-hydrate on success.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    admin: null,
    tenant: null,
    loading: true,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get('/api/me');
      setState({
        admin: data.admin,
        tenant: data.tenant,
        loading: false,
        error: null,
      });
      return data;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // Not logged in — this is the expected state for signup/verify/login pages.
        setState({ admin: null, tenant: null, loading: false, error: null });
        return null;
      }
      setState({ admin: null, tenant: null, loading: false, error: e });
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = {
    ...state,
    isAuthenticated: !!state.admin,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
